import {
  AggregationType,
  BottomLineAggregatesRow,
  DataRow,
  isInstance,
  isReport,
  ProcessedMetrics,
  type ReportTableProps,
  RowType,
  type SingleDayRow,
  type SingleJobRow,
} from "./table/table.types";
import { areArraysEqual } from "portal/utils/equality";
import {
  booleanValueFormatter,
  CarbonDataGrid,
  excelFormatter,
  getRenderDateTime,
} from "portal/components/CarbonDataGrid";
import { Breakpoint, useBreakpoint } from "portal/utils/hooks/useBreakpoint";
import { CarbonDataCell } from "./table/CarbonDataCell";
import { CarbonGroupingCell } from "./table/CarbonGroupingCell";
import {
  CarbonListCell,
  ListValue,
  listValueText,
} from "portal/components/table/CarbonListCell";
import { CarbonUnit } from "portal/utils/units/units";
import { checkNever, isNil, isUndefined } from "portal/utils/identity";
import { classes } from "portal/theme/theme";
import { DateTime } from "luxon";
import {
  FeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { filterNotNil } from "../../utils/arrays";
import { formatError } from "portal/utils/errors";
import { formatMetric, formatString } from "../measurement/formatters";
import { getAggregates } from "./table/getAggregates";
import {
  getDateRange,
  getDatesFromRange,
  getName,
  getRobotColorClass,
  getRobotIds,
  getShowAverage,
  getShowTotal,
  getVisibleColumns,
} from "./table/helpers";
import { getDateRangeDescription } from "../CarbonDateRangePicker";
import {
  getDecimalPlacesForCycleSlot,
  getSavedUnit,
} from "../measurement/numericUnits";
import {
  getMetricById,
  getMetricName,
  isMetricEnabled,
  METRIC_BANDING_CONFIG,
  METRIC_BANDING_PROFILES,
  METRIC_CROP,
  METRIC_CROPS,
  METRIC_JOB_NAME,
  METRIC_JOBS,
  METRIC_WEED_TYPES,
} from "portal/utils/certifiedMetrics";
import { getMetricValues } from "./table/getMetricValues";
import { getSelectedRobotsDescription } from "./ReportTools";
import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridColType,
  GridCsvGetRowsToExportParams,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  GridToolbarExport,
  renderBooleanCell,
} from "@mui/x-data-grid-premium";
import { isMetricNumber, Metric, MetricList } from "portal/utils/metrics";
import { JigsawDataSourceIcon } from "../JigsawDataSourceIcon";
import { MetricDataSource } from "protos/portal/metrics";
import {
  normalizeVisibleColumns,
  PINNED_COLUMN_DATE,
  PINNED_COLUMN_JOB,
  PINNED_COLUMN_ROBOT,
  useDateRange,
} from "portal/utils/reports";
import { Paper } from "@mui/material";
import { portalApi } from "portal/state/portalApi";
import { ReportMode } from "protos/portal/reports";
import { RobotResponse } from "protos/portal/robots";
import { TFunction } from "i18next";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useMeasurement, useSelf } from "portal/state/store";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useReportTableDailyMetrics } from "./table/useReportTableDailyMetrics";
import { useReportTableRobotJobs } from "./table/useReportTableRobotJobs";
import { useStableArray } from "portal/utils/hooks/useStable";
import { useTranslation } from "react-i18next";
import ErrorIcon from "@mui/icons-material/WarningOutlined";
import React, { FunctionComponent, useCallback, useMemo, useRef } from "react";

const defaultColumn: Partial<GridColDef> = {
  sortable: true,
  disableColumnMenu: true,
};

const CARBON_LIST_CELL_MIN_WIDTH = 125;

// The grid has no enum column type. The string type makes the quick filter
// search the formatted label. The value itself stays the numeric enum, so the
// default comparator sorts it in enum order.
const GRID_COLUMN_TYPE: Record<Metric["type"], GridColType> = {
  boolean: "boolean",
  enum: "string",
  list: "custom",
  number: "number",
  string: "string",
};

// Booleans export TRUE/FALSE. Enums export the translated label. Everything
// else is already a string from the value getter.
const metricValueFormatter = (
  metric: Metric,
  t: TFunction
): NonNullable<GridColDef<DataRow>["valueFormatter"]> => {
  if (metric.type === "boolean") {
    return booleanValueFormatter;
  }
  if (metric.type === "enum") {
    return (value: unknown) =>
      metric.formatValue(typeof value === "number" ? value : undefined, t) ??
      "";
  }
  return String;
};

export const ReportTable: FunctionComponent<ReportTableProps> = (props) => {
  const { loading = false, onColumnOrderChange } = props;
  const { isInternal, measurementSystem } = useSelf();
  const { cycleSlots } = useMeasurement();

  const breakpoint = useBreakpoint();
  const isSmall = breakpoint <= Breakpoint.sm;

  const { t, i18n } = useTranslation();

  const useJigsaw = useJigsawMetrics();
  const reportCustomerUuid = isReport(props)
    ? props.report?.customerUuid
    : props.customerUuid;
  const { isEnabled: entryTimingEnabled } = useFeatureFlagForCustomer(
    FeatureFlag.ENTRY_TIMING,
    reportCustomerUuid
  );

  const { data: summaries } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery(
      isInstance(props)
        ? {
            excludeMetrics: false,
            instance: props.instance?.slug,
            language: i18n.language,
            useJigsaw,
          }
        : { excludeMetrics: false, language: i18n.language, useJigsaw }
    )
  );

  // we have to stabilize the robotIds array to prevent unnecessary re-renders
  // otherwise, we use utility functions that accept props but they change on
  // every render so we can't use them as dependencies
  const robotIds = useStableArray(getRobotIds(props));

  const mode = useMemo<ReportMode>(() => {
    let mode: ReportMode | undefined;
    if (isInstance(props)) {
      mode = props.instance?.mode;
    }
    if (isReport(props)) {
      mode = props.report?.mode;
    }
    return mode || ReportMode.REPORT_MODE_DAYS;
  }, [props]);

  const dateRange = useDateRange(getDateRange(props));
  const selectedRobots = useMemo<RobotResponse[]>(() => {
    if (!summaries) {
      return [];
    }
    return summaries
      .filter(
        ({ robot }) =>
          robot &&
          robot.db?.id &&
          robotIds.includes(robot.db.id) &&
          robot.serial
      )
      .map(({ robot }) => robot)
      .filter((robot) => !isUndefined(robot));
  }, [robotIds, summaries]);

  const visibleColumnsCacheRef = useRef<string[] | undefined>();
  const visibleColumns: string[] = (() => {
    const result = normalizeVisibleColumns(getVisibleColumns(props), useJigsaw);
    const { current: last } = visibleColumnsCacheRef;
    if (last === undefined || !areArraysEqual(result, last)) {
      visibleColumnsCacheRef.current = result;
      return result;
    }
    return last;
  })();

  const visibleMetrics: Metric[] = useMemo(() => {
    return visibleColumns.flatMap((id) => {
      if (!isMetricEnabled(id, { entryTimingEnabled })) {
        return [];
      }
      const metric = getMetricById(id, mode);
      return isUndefined(metric) ? [] : [metric];
    });
  }, [entryTimingEnabled, mode, visibleColumns]);

  const {
    dayMetrics,
    dayMetricsError,
    fetchDailyMetricsBody,
    isDayMetricsLoading,
  } = useReportTableDailyMetrics({
    dateRange,
    mode,
    selectedRobots,
    summaries,
    useJigsaw,
    visibleMetrics,
  });

  const {
    fetchJobMetricsBody,
    isJobsDataLoading,
    jobMetricsError,
    jobsBySerial,
  } = useReportTableRobotJobs({
    dateRange,
    mode,
    selectedRobots,
    useJigsaw,
    visibleMetrics,
  });

  /**
   * Table
   */
  const isLoading =
    !summaries ||
    (mode === ReportMode.REPORT_MODE_JOBS &&
      fetchJobMetricsBody &&
      !jobMetricsError &&
      isJobsDataLoading) ||
    (mode === ReportMode.REPORT_MODE_DAYS &&
      fetchDailyMetricsBody &&
      !dayMetricsError &&
      isDayMetricsLoading) ||
    loading;
  const tableData = useMemo<
    | {
        rows: DataRow[];
        totals: BottomLineAggregatesRow;
        averages: BottomLineAggregatesRow;
        loadedRobots: number[];
      }
    | undefined
  >(() => {
    if (isLoading) {
      return;
    }
    let rows: DataRow[] = [];
    const allMetrics: ProcessedMetrics[] = [];
    const loadedRobotsSet: Set<number> = new Set();
    for (const robot of selectedRobots) {
      const internalSerial = robot.serial;
      const singleRows: Array<SingleDayRow | SingleJobRow> = [];
      if (mode === ReportMode.REPORT_MODE_JOBS) {
        if (!jobsBySerial[internalSerial]) {
          continue;
        }
        for (const job of jobsBySerial[internalSerial]) {
          const metrics = getMetricValues(job);
          allMetrics.push(metrics);
          singleRows.push({
            type: RowType.SINGLE_JOB,
            serial: internalSerial,
            customerSerial: robot.customerSerial,
            jobId: job.jobId,
            jobName: job.jobName,
            jobStartTimestampMs: job.jobStartTimestampMs,
            jobStopTimestampMs: job.jobStopTimestampMs,
            metrics,
          });
        }
      } else if (mode === ReportMode.REPORT_MODE_DAYS) {
        if (!dayMetrics) {
          return;
        }
        for (const date of getDatesFromRange(dateRange)) {
          if (!(robot.serial in dayMetrics.metrics)) {
            continue;
          }
          const robotMetrics = dayMetrics.metrics[internalSerial];
          if (!robotMetrics) {
            continue;
          }
          const dailyMetric = robotMetrics.metrics[date];
          if (!dailyMetric || !dailyMetric.weedingUptimeSeconds) {
            continue;
          }
          const metrics = getMetricValues(dailyMetric);
          allMetrics.push(metrics);
          singleRows.push({
            type: RowType.SINGLE_DAY,
            serial: internalSerial,
            customerSerial: robot.customerSerial,
            date,
            metrics,
          });
        }
      }
      if (singleRows.length > 0) {
        const { metrics, serial } = singleRows[0] ?? {};
        if (!metrics || !serial) {
          continue;
        }
        const robotId = summaries.find(({ robot }) => robot?.serial === serial)
          ?.robot?.db?.id;
        if (!robotId) {
          console.error(`Missing robot ID for serial ${serial}`);
          continue;
        }
        loadedRobotsSet.add(robotId);
        const baseFields = { serial, robotId };
        const aggregates = getAggregates(
          singleRows.map((row) => row.metrics),
          baseFields
        );
        rows.push({
          type: RowType.ROBOT_AGGREGATES,
          serial: internalSerial,
          customerSerial: robot.customerSerial,
          aggregates,
        });
      }
      rows.push(...singleRows);
    }

    const loadedRobots = [...loadedRobotsSet];
    if (loadedRobots.length <= 1) {
      rows = rows.filter((row) => row.type !== RowType.ROBOT_AGGREGATES);
    }

    const aggregates = getAggregates(allMetrics, {
      serial: "",
      robotId: -1,
    });
    const totals: BottomLineAggregatesRow = {
      type: RowType.BOTTOM_LINE_AGGREGATES,
      aggregationType: AggregationType.TOTAL,
      aggregates,
    };
    const averages: BottomLineAggregatesRow = {
      type: RowType.BOTTOM_LINE_AGGREGATES,
      aggregationType: AggregationType.AVERAGE,
      aggregates,
    };
    return {
      rows,
      totals,
      averages,
      loadedRobots,
    };
  }, [
    isLoading,
    selectedRobots,
    mode,
    jobsBySerial,
    dayMetrics,
    dateRange,
    summaries,
  ]);

  const showTotal = getShowTotal(props);
  const showAverage = getShowAverage(props);

  const dataSourceColumn:
    | GridColDef<DataRow, MetricDataSource | undefined>
    | undefined = useMemo(
    () =>
      isInternal && useJigsaw
        ? {
            ...defaultColumn,
            align: "center",
            field: "dataSource",
            headerName: t("components.JobSummary.dataSource"),
            type: "custom",
            renderCell: ({ value }) => (
              <JigsawDataSourceIcon
                dataSource={value}
                style={{ marginTop: "13px" }}
              />
            ),
            valueGetter: (_value, params) =>
              params.type === RowType.SINGLE_DAY ||
              params.type === RowType.SINGLE_JOB
                ? params.metrics.dataSource
                : undefined,
          }
        : undefined,
    [isInternal, t, useJigsaw]
  );

  const dateColumn: GridColDef<DataRow> | undefined = useMemo(() => {
    return {
      ...defaultColumn,
      field: PINNED_COLUMN_DATE,
      headerName: t("views.reports.scheduled.table.fields.date"),
      cellClassName: "font-bold",
      valueGetter: (_value, row): string => {
        // Used for exports, and also for the actual date column values
        // when there is only one robot. When there are multiple robots,
        // this is hidden by the `columnVisibilityModel`.
        switch (row.type) {
          case RowType.SINGLE_DAY: {
            return row.date;
          }
          case RowType.ROBOT_AGGREGATES: {
            return "";
          }
          case RowType.BOTTOM_LINE_AGGREGATES: {
            // "Total"/"Average": not a date, but visually we want it in this column
            return t(row.aggregationType);
          }
          case RowType.SINGLE_JOB: {
            console.warn("Unexpected job row in date column:", row);
            return "";
          }
        }
      },
      width: 150,
    };
  }, [t]);

  const jobColumns: GridColDef<DataRow>[] | undefined = useMemo(() => {
    return filterNotNil([
      {
        ...defaultColumn,
        field: PINNED_COLUMN_JOB,
        headerName: t("models.jobs.job_title_one"),
        cellClassName: "font-bold",
        valueGetter: (_value, row): string => {
          // Used for exports, and also for the actual job column values
          // when there is only one robot. When there are multiple robots,
          // this is hidden by the `columnVisibilityModel`.
          switch (row.type) {
            case RowType.SINGLE_JOB: {
              return row.jobName;
            }
            case RowType.ROBOT_AGGREGATES: {
              return "";
            }
            case RowType.BOTTOM_LINE_AGGREGATES: {
              // "Total"/"Average": not a date, but visually we want it in this column
              return t(row.aggregationType);
            }
            case RowType.SINGLE_DAY: {
              console.warn("Unexpected date row in job column:", row);
              return "";
            }
          }
        },
        width: 150,
      },
      {
        ...defaultColumn,
        field: "timestamp_ms",
        headerName: t("components.DateRangePicker.startDate"),
        type: "dateTime",
        valueGetter: (_value, row) => {
          if (row.type === RowType.SINGLE_JOB) {
            return row.jobStartTimestampMs
              ? DateTime.fromMillis(row.jobStartTimestampMs)
              : undefined;
          }
        },
        valueFormatter: excelFormatter,
        renderCell: getRenderDateTime(i18n, "short"),
      },
      {
        ...defaultColumn,
        field: "stop_timestamp_ms",
        headerName: t("components.DateRangePicker.endDate"),
        type: "dateTime",
        valueGetter: (_value, row) => {
          if (row.type === RowType.SINGLE_JOB) {
            return row.jobStopTimestampMs
              ? DateTime.fromMillis(row.jobStopTimestampMs)
              : undefined;
          }
        },
        valueFormatter: excelFormatter,
        renderCell: getRenderDateTime(i18n, "short"),
      },
      dataSourceColumn,
    ]);
  }, [dataSourceColumn, i18n, t]);

  // Pre-select units for each metric, which are used for CSV exports in
  // both the header and the data cells, to ensure that those codepaths
  // agree on what unit is used.
  interface MetricUnits {
    carbonUnits: CarbonUnit;
    headerUnits: string;
  }
  const metricUnits: Map<string, MetricUnits> = useMemo(() => {
    const metricUnits = new Map();
    for (const metric of visibleMetrics) {
      let carbonUnits;
      let headerUnits = "";

      if (isMetricNumber(metric)) {
        const { cycleSlot } = metric;
        const { unit: toUnits } = getSavedUnit(
          measurementSystem,
          cycleSlots,
          cycleSlot,
          metric.units
        );
        const formatted = formatMetric(t, i18n, measurementSystem, metric, 1, {
          decimalPlaces: getDecimalPlacesForCycleSlot(cycleSlot, toUnits),
          toUnits,
        });
        carbonUnits = formatted.carbonUnits;
        headerUnits = formatted.units;
      }
      metricUnits.set(metric.id, { carbonUnits, headerUnits });
    }
    return metricUnits;
  }, [t, i18n, visibleMetrics, cycleSlots, measurementSystem]);

  const getMetricHeaderName = useCallback(
    (metric: Metric): string => {
      const { headerUnits = "" } = metricUnits.get(metric.id) ?? {};
      const unitsPart = headerUnits ? ` (${headerUnits})` : "";
      const metricName = getMetricName(t, metric);
      return `${metricName}${unitsPart}`;
    },
    [t, metricUnits]
  );

  const listColDef = useCallback(
    (
      metric: Metric,
      listMetric: MetricList,
      values: (row: SingleDayRow | SingleJobRow) => ListValue[]
    ): GridColDef<DataRow> => ({
      ...defaultColumn,
      type: "custom",
      field: metric.id,
      minWidth: CARBON_LIST_CELL_MIN_WIDTH,
      headerName: getMetricHeaderName(listMetric),
      valueGetter: (_value, row) => {
        if (
          row.type === RowType.BOTTOM_LINE_AGGREGATES ||
          row.type === RowType.ROBOT_AGGREGATES
        ) {
          return;
        }
        return values(row)
          .map((v) => listValueText(v))
          .join(",");
      },
      renderCell(props) {
        if (
          props.row.type === RowType.SINGLE_DAY ||
          props.row.type === RowType.SINGLE_JOB
        ) {
          return <CarbonListCell {...props} values={values(props.row)} />;
        }
        return (
          <CarbonDataCell
            // CarbonDataCell can take list metrics so long as it's an aggregate row
            // we don't really do true aggregations for lists, but this'll display
            // (MIXED) if multiple values are displayed
            metric={metric as any}
            row={props.row}
            showTotals={showTotal}
            showAverages={showAverage}
          />
        );
      },
      sortable: false,
    }),
    [getMetricHeaderName, showAverage, showTotal]
  );

  const columns: GridColDef<DataRow>[] = useMemo(() => {
    return [
      {
        ...defaultColumn,
        field: "serial",
        headerName: t("models.robots.fields.serial"),
        valueGetter: (_value, row): string => {
          // Used for exports; hidden from UI by `columnVisibilityModel`.
          switch (row.type) {
            case RowType.SINGLE_JOB:
            case RowType.SINGLE_DAY:
            case RowType.ROBOT_AGGREGATES: {
              const { customerSerial } = row;
              return customerSerial;
            }
            case RowType.BOTTOM_LINE_AGGREGATES: {
              return "";
            }
          }
        },
      },
      ...(mode === ReportMode.REPORT_MODE_DAYS
        ? filterNotNil([dateColumn, dataSourceColumn])
        : []),
      ...(mode === ReportMode.REPORT_MODE_JOBS ? jobColumns : []),
      ...visibleMetrics.map((metric): GridColDef<DataRow> => {
        if (useJigsaw) {
          // eslint-disable-next-line unicorn/prefer-switch
          if (metric === METRIC_BANDING_CONFIG) {
            return listColDef(
              metric,
              METRIC_BANDING_PROFILES,
              (row) => row.metrics.bandingProfiles
            );
          } else if (metric === METRIC_CROP) {
            return listColDef(metric, METRIC_CROPS, (row) =>
              row.metrics.crops.map((crop) => ({
                id: crop.id,
                name: crop.localizedName,
              }))
            );
          } else if (metric === METRIC_JOB_NAME) {
            return listColDef(metric, METRIC_JOBS, (row) =>
              row.type === RowType.SINGLE_DAY
                ? row.metrics.jobs
                : [
                    {
                      id: row.jobId,
                      name: row.jobName,
                    },
                  ]
            );
          } else if (metric === METRIC_WEED_TYPES) {
            return listColDef(metric, METRIC_WEED_TYPES, (row) =>
              (METRIC_WEED_TYPES.getValue(row.metrics) ?? []).map((value) => {
                if (value.percent === undefined) {
                  return value;
                }
                return {
                  id: value.id,
                  name: value.name,
                  suffix: `: ${value.percent.toFixed(1)}${
                    i18n.language.startsWith("fr") ? " %" : "%"
                  }`,
                };
              })
            );
          }
        }

        return {
          ...defaultColumn,
          type: GRID_COLUMN_TYPE[metric.type],
          field: metric.id,
          headerName: getMetricHeaderName(metric),
          renderHeader: () => <span>{getMetricName(t, metric)}</span>,
          valueFormatter: metricValueFormatter(metric, t),
          valueGetter: (_value, row) => {
            // This is used for exports and boolean metrics, since we have a custom cell renderer for other columns
            switch (row.type) {
              case RowType.SINGLE_JOB:
              case RowType.SINGLE_DAY: {
                const metrics: ProcessedMetrics = row.metrics;
                const value = metrics[metric.id];
                if (metric.type === "boolean") {
                  return value;
                }
                if (metric.type === "enum") {
                  // The number sorts in enum order. valueFormatter renders the label.
                  return typeof value === "number" ? value : undefined;
                }
                const unitData = metricUnits.get(metric.id);
                if (!unitData) {
                  console.error(
                    "Missing unit data for metric:",
                    metric.id,
                    row
                  );
                  return String(value);
                }
                const toUnits = unitData.carbonUnits;
                const formatted = isNil(value)
                  ? formatString("")
                  : formatMetric(t, i18n, measurementSystem, metric, value, {
                      toUnits,
                      decimalPlaces: getDecimalPlacesForCycleSlot(
                        metric.cycleSlot,
                        toUnits
                      ),
                      forExport: true,
                    });
                const { converted, value: stringValue } = formatted;
                return converted !== undefined && !Number.isNaN(converted)
                  ? String(converted)
                  : stringValue;
              }
              case RowType.ROBOT_AGGREGATES:
              case RowType.BOTTOM_LINE_AGGREGATES: {
                if (metric.type === "boolean" || metric.type === "enum") {
                  return row.aggregates.unanimous[metric.id];
                }
                // Shouldn't matter; we only export non-grouped rows.
                return;
              }
              default: {
                console.error(
                  "Unexpected row type in metric column:",
                  checkNever(row)
                );
              }
            }
          },
          renderCell:
            metric.type === "boolean"
              ? renderBooleanCell
              : metric.type === "list"
              ? ({ row }) =>
                  row.type === RowType.SINGLE_DAY ||
                  row.type === RowType.SINGLE_JOB ? (
                    <CarbonListCell
                      values={metric.getValue(row.metrics) ?? []}
                    />
                  ) : (
                    <CarbonDataCell
                      // CarbonDataCell can take list metrics so long as it's an aggregate row
                      // we don't really do true aggregations for lists, but this'll display
                      // (MIXED) if multiple values are displayed
                      metric={metric as any}
                      row={row}
                      showTotals={showTotal}
                      showAverages={showAverage}
                    />
                  )
              : ({ row }) => {
                  return (
                    <CarbonDataCell
                      metric={metric}
                      row={row}
                      showTotals={showTotal}
                      showAverages={showAverage}
                    />
                  );
                },
        };
      }),
    ];
  }, [
    t,
    dateColumn,
    dataSourceColumn,
    mode,
    jobColumns,
    visibleMetrics,
    getMetricHeaderName,
    listColDef,
    metricUnits,
    i18n,
    measurementSystem,
    showTotal,
    showAverage,
    useJigsaw,
  ]);

  let errorMessage: string | undefined;

  if (robotIds.length === 0) {
    errorMessage = t("views.reports.scheduled.table.errors.noRobots");
  } else if (!dateRange[0]) {
    errorMessage = t("views.reports.scheduled.table.errors.noStartDate");
  } else if (!dateRange[1]) {
    errorMessage = t("views.reports.scheduled.table.errors.noEndDate");
  } else if (getVisibleColumns(props).length === 0) {
    errorMessage = t("views.reports.scheduled.table.errors.noColumns");
  }

  if (!isNil(errorMessage)) {
    return (
      <Paper className="flex-1 flex items-center justify-center">
        <div className="flex flex-col gap-4 items-center">
          <ErrorIcon className="text-6xl" />
          {formatError(t, errorMessage)}
        </div>
      </Paper>
    );
  }

  return (
    <CarbonDataGrid<DataRow>
      treeData={tableData && tableData.loadedRobots.length > 1}
      getTreeDataPath={(row) => {
        switch (row.type) {
          case RowType.SINGLE_JOB: {
            return [row.serial, row.jobId];
          }
          case RowType.SINGLE_DAY: {
            return [row.serial, row.date];
          }
          case RowType.ROBOT_AGGREGATES: {
            return [row.serial];
          }
          case RowType.BOTTOM_LINE_AGGREGATES: {
            return [row.aggregationType];
          }
          default: {
            console.error("Unexpected row type in tree:", checkNever(row));
            return [];
          }
        }
      }}
      disableColumnReorder
      disableRowSelectionOnClick
      onColumnOrderChange={onColumnOrderChange}
      groupingColDef={{
        headerName:
          mode === ReportMode.REPORT_MODE_DAYS
            ? t("views.reports.scheduled.table.fields.group")
            : t("views.reports.scheduled.table.fields.groupJob"),
        cellClassName: ({ row }) => {
          const baseClasses = "font-bold";
          switch (row.type) {
            case RowType.SINGLE_JOB:
            case RowType.SINGLE_DAY: {
              const robotId =
                summaries?.find(({ robot }) => robot?.serial === row.serial)
                  ?.robot?.db?.id ?? -1;
              const color = getRobotColorClass(
                tableData?.loadedRobots ?? [],
                robotId
              );
              return classes(baseClasses, color);
            }
            case RowType.ROBOT_AGGREGATES:
            case RowType.BOTTOM_LINE_AGGREGATES: {
              return baseClasses;
            }
          }
        },
        renderCell: (parameters) => (
          <CarbonGroupingCell
            {...parameters}
            showTotals={showTotal}
            showAverages={showAverage}
          />
        ),
        width: 150,
      }}
      columnVisibilityModel={{
        serial: false,
        [PINNED_COLUMN_DATE]:
          !isUndefined(tableData) && tableData.loadedRobots.length <= 1,
        [PINNED_COLUMN_JOB]:
          !isUndefined(tableData) && tableData.loadedRobots.length <= 1,
      }}
      header={
        <>
          <div />
          <GridToolbarExport
            csvOptions={{
              fileName: `${getName(t, props)} - ${getSelectedRobotsDescription(
                t,
                tableData?.loadedRobots,
                summaries
              )} - ${getDateRangeDescription(getDateRange(props))}`,
              fields: [
                "serial",
                ...(mode === ReportMode.REPORT_MODE_DAYS
                  ? [PINNED_COLUMN_DATE]
                  : jobColumns.map((column) => column.field)),
                ...visibleMetrics.map((metric) => metric.id),
              ],
              getRowsToExport: ({ apiRef }: GridCsvGetRowsToExportParams) => {
                const rows = gridFilteredSortedRowIdsSelector(apiRef);
                const tree = gridRowTreeSelector(apiRef);
                return rows.filter(
                  (rowId) => tree[rowId] && tree[rowId].type !== "group"
                );
              },
            }}
            excelOptions={{ disableToolbarButton: true }}
            printOptions={{ disableToolbarButton: true }}
          />
        </>
      }
      className="flex flex-1"
      getRowId={(row) => {
        switch (row.type) {
          case RowType.SINGLE_DAY: {
            return `t0-${row.serial}-${row.date}`;
          }
          case RowType.ROBOT_AGGREGATES: {
            return `t1-${row.serial}`;
          }
          case RowType.BOTTOM_LINE_AGGREGATES: {
            return `t2-${row.aggregationType}`;
          }
          case RowType.SINGLE_JOB: {
            return `t3-${row.jobId}`;
          }
        }
      }}
      pinnedColumns={{
        left: isSmall
          ? []
          : [
              GRID_TREE_DATA_GROUPING_FIELD,
              PINNED_COLUMN_ROBOT,
              PINNED_COLUMN_DATE,
              PINNED_COLUMN_JOB,
            ],
      }}
      loading={isLoading || !tableData}
      rows={tableData?.rows ?? []}
      columns={columns}
      totals={showTotal ? tableData?.totals : undefined}
      averages={showAverage ? tableData?.averages : undefined}
      hideFooter
    />
  );
};
